import { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Grid,
  Modal,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  IconButton,
  Fade,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { IAssetReport } from "../../config/types";
import useAppNavigation from "../../hooks/useAppNavigation";
import { MainPageBase } from "../page-bases/MainPageBase";
import LabelButton from "../../components/Button/LabelButton";
import appRoutePath from "../../config/path";
import SimpleLabel from "../../components/Label";
import { assetReportApi } from "../../services/asset-report/index";
import { formatDate } from "../../utils/common";
import { ExpandableContainer } from "../../components/Collapsible/ExpandableContainer";

interface ImageModalData {
  url: string;
  type: string;
  size: number;
  index: number;
}

const AssetReportDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<IAssetReport | null>(null);
  const [imageModal, setImageModal] = useState<ImageModalData | null>(null);
  const navigate = useAppNavigation();

  const fetchData = async () => {
    const res = await assetReportApi.getAssetReport(id ?? "");
    if (res.data) {
      setData(res.data);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleImageClick = (image: any, index: number) => {
    setImageModal({
      url: image.signature_url,
      type: image.file_type,
      size: image.file_size,
      index: index,
    });
  };

  const handleCloseModal = () => {
    setImageModal(null);
  };

  if (!data) {
    return null;
  }

  return (
    <MainPageBase
      pageState="main"
      title="Asset Report Details"
      subtitle="Detailed information about the asset report including all associated assets and evidence."
      extraComponent={
        <LabelButton
          onPress={() =>
            navigate({
              path: appRoutePath.ASSET_REPORT_HISTORY,
            })
          }
          label="Back"
          direction="left"
        />
      }
    >
      <Box height="100%" display="flex" justifyContent="center">
        <Stack width="100%" spacing={0}>
          {/* Asset Report Information - NOW AS TABLE */}
          <Box mb={3}>
            <ExpandableContainer
              label="Asset Report Information"
              initOpenState={true}
            >
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                }}
              >
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 400,
                          width: "200px",
                          bgcolor: "#f8f9fa",
                          fontFamily: "Inter",
                          fontSize: 14,
                          color: "#666666",
                        }}
                      >
                        Issue
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          fontFamily: "Inter",
                          fontSize: 14,
                          color: "#333333",
                        }}
                      >
                        {data.issue}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 400,
                          width: "200px",
                          bgcolor: "#f8f9fa",
                          fontFamily: "Inter",
                          fontSize: 14,
                          color: "#666666",
                        }}
                      >
                        Comments
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          fontFamily: "Inter",
                          fontSize: 14,
                          color: "#333333",
                        }}
                      >
                        {data.comments || "N/A"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 400,
                          width: "200px",
                          bgcolor: "#f8f9fa",
                          fontFamily: "Inter",
                          fontSize: 14,
                          color: "#666666",
                        }}
                      >
                        Resolution
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          fontFamily: "Inter",
                          fontSize: 14,
                          color: "#333333",
                        }}
                      >
                        {data.resolution || "N/A"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 400,
                          width: "200px",
                          bgcolor: "#f8f9fa",
                          fontFamily: "Inter",
                          fontSize: 14,
                          color: "#666666",
                        }}
                      >
                        Date Reported
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          fontFamily: "Inter",
                          fontSize: 14,
                          color: "#333333",
                        }}
                      >
                        {formatDate(data.created_at)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 400,
                          width: "200px",
                          bgcolor: "#f8f9fa",
                          fontFamily: "Inter",
                          fontSize: 14,
                          color: "#666666",
                        }}
                      >
                        Last Updated
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          fontFamily: "Inter",
                          fontSize: 14,
                          color: "#333333",
                        }}
                      >
                        {formatDate(data.updated_at)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 400,
                          width: "200px",
                          bgcolor: "#f8f9fa",
                          fontFamily: "Inter",
                          fontSize: 14,
                          color: "#666666",
                        }}
                      >
                        Total Assets
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          fontFamily: "Inter",
                          fontSize: 14,
                          color: "#333333",
                        }}
                      >
                        {data.assets?.length.toString() || "0"}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </ExpandableContainer>
          </Box>

          {/* Evidence Images */}
          {data.evidence_images && data.evidence_images.length > 0 && (
            <Box mb={3}>
              <ExpandableContainer
                label={`Evidence Images (${data.evidence_images.length})`}
                initOpenState={true}
              >
                <Grid
                  container
                  spacing={3}
                  width="100%"
                  alignItems="flex-start"
                >
                  {data.evidence_images.map((image, index) => (
                    <Grid item xs={12} sm={6} md={4} key={image.id}>
                      <Card
                        sx={{
                          boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                          borderRadius: 2,
                          transition: "all 0.3s ease",
                          cursor: "pointer",
                          "&:hover": {
                            boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                            transform: "translateY(-4px)",
                          },
                        }}
                        onClick={() => handleImageClick(image, index)}
                      >
                        <Box
                          component="img"
                          src={image.signature_url}
                          alt={`Evidence ${index + 1}`}
                          sx={{
                            width: "100%",
                            height: "200px",
                            objectFit: "cover",
                          }}
                        />
                        <CardContent sx={{ pt: 2, pb: 2 }}>
                          <Stack spacing={0.5}>
                            <SimpleLabel
                              title={`Image ${index + 1}`}
                              fontWeight={600}
                              fontSize={14}
                            />
                            <SimpleLabel
                              title={`Type: ${image.file_type}`}
                              fontWeight={400}
                              fontSize={12}
                            />
                            <SimpleLabel
                              title={`Size: ${(image.file_size / 1024).toFixed(2)} KB`}
                              fontWeight={400}
                              fontSize={12}
                            />
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </ExpandableContainer>
            </Box>
          )}

          {/* Associated Assets */}
          {data.assets && data.assets.length > 0 && (
            <>
              {data.assets.map((asset, index) => (
                <Box key={asset.id} mb={3}>
                  <ExpandableContainer
                    label={`Asset ${index + 1}: ${asset.brand} ${asset.model} - ${asset.serial_number}`}
                    initOpenState={index === 0}
                    isTable={true}
                  >
                    <Stack spacing={3} width="100%" alignItems="flex-start">
                      {/* Basic Asset Information */}
                      <Box width="100%">
                        <SimpleLabel
                          title="Basic Information"
                          fontWeight={600}
                          fontSize={16}
                          sx={{ mt: 2 }}
                        />
                        <Box mt={1.5}>
                          <TableContainer
                            component={Paper}
                            elevation={0}
                            sx={{
                              border: "1px solid #e0e0e0",
                              borderRadius: 2,
                            }}
                          >
                            <Table>
                              <TableBody>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      fontWeight: 400,
                                      width: "200px",
                                      bgcolor: "#f8f9fa",
                                      fontFamily: "Inter",
                                      fontSize: 14,
                                      color: "#666666",
                                    }}
                                  >
                                    Serial Number
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 500,
                                      fontFamily: "Inter",
                                      fontSize: 14,
                                      color: "#333333",
                                    }}
                                  >
                                    {asset.serial_number}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      fontWeight: 400,
                                      width: "200px",
                                      bgcolor: "#f8f9fa",
                                      fontFamily: "Inter",
                                      fontSize: 14,
                                      color: "#666666",
                                    }}
                                  >
                                    Embossment Number
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 500,
                                      fontFamily: "Inter",
                                      fontSize: 14,
                                      color: "#333333",
                                    }}
                                  >
                                    {asset.embossment_number}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      fontWeight: 400,
                                      width: "200px",
                                      bgcolor: "#f8f9fa",
                                      fontFamily: "Inter",
                                      fontSize: 14,
                                      color: "#666666",
                                    }}
                                  >
                                    Brand
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 500,
                                      fontFamily: "Inter",
                                      fontSize: 14,
                                      color: "#333333",
                                    }}
                                  >
                                    {asset.brand}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      fontWeight: 400,
                                      width: "200px",
                                      bgcolor: "#f8f9fa",
                                      fontFamily: "Inter",
                                      fontSize: 14,
                                      color: "#666666",
                                    }}
                                  >
                                    Model
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 500,
                                      fontFamily: "Inter",
                                      fontSize: 14,
                                      color: "#333333",
                                    }}
                                  >
                                    {asset.model}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      fontWeight: 400,
                                      width: "200px",
                                      bgcolor: "#f8f9fa",
                                      fontFamily: "Inter",
                                      fontSize: 14,
                                      color: "#666666",
                                    }}
                                  >
                                    Category
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 500,
                                      fontFamily: "Inter",
                                      fontSize: 14,
                                      color: "#333333",
                                    }}
                                  >
                                    {asset.asset_category_name || "N/A"}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      </Box>

                      {/* Status Information */}
                      <Box width="100%">
                        <SimpleLabel
                          title="Status Information"
                          fontWeight={600}
                          fontSize={16}
                          sx={{ mt: 2 }}
                        />
                        <Box mt={1.5}>
                          <TableContainer
                            component={Paper}
                            elevation={0}
                            sx={{
                              border: "1px solid #e0e0e0",
                              borderRadius: 2,
                            }}
                          >
                            <Table>
                              <TableBody>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      fontWeight: 400,
                                      width: "200px",
                                      bgcolor: "#f8f9fa",
                                      fontFamily: "Inter",
                                      fontSize: 14,
                                      color: "#666666",
                                    }}
                                  >
                                    Asset Status
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 500,
                                      fontFamily: "Inter",
                                      fontSize: 14,
                                      color: "#333333",
                                    }}
                                  >
                                    {asset.asset_status}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      fontWeight: 400,
                                      width: "200px",
                                      bgcolor: "#f8f9fa",
                                      fontFamily: "Inter",
                                      fontSize: 14,
                                      color: "#666666",
                                    }}
                                  >
                                    Distribution Status
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 500,
                                      fontFamily: "Inter",
                                      fontSize: 14,
                                      color: "#333333",
                                    }}
                                  >
                                    {asset.distribution_status}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      fontWeight: 400,
                                      width: "200px",
                                      bgcolor: "#f8f9fa",
                                      fontFamily: "Inter",
                                      fontSize: 14,
                                      color: "#666666",
                                    }}
                                  >
                                    Assignment Status
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 500,
                                      fontFamily: "Inter",
                                      fontSize: 14,
                                      color: "#333333",
                                    }}
                                  >
                                    {asset.assignment_status}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      fontWeight: 400,
                                      width: "200px",
                                      bgcolor: "#f8f9fa",
                                      fontFamily: "Inter",
                                      fontSize: 14,
                                      color: "#666666",
                                    }}
                                  >
                                    Ownership
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 500,
                                      fontFamily: "Inter",
                                      fontSize: 14,
                                      color: "#333333",
                                    }}
                                  >
                                    {asset.ownership}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      </Box>

                      {/* Location Information */}
                      <Box width="100%">
                        <SimpleLabel
                          title="Location Information"
                          fontWeight={600}
                          fontSize={16}
                          sx={{ mt: 2 }}
                        />
                        <Box mt={1.5}>
                          <TableContainer
                            component={Paper}
                            elevation={0}
                            sx={{
                              border: "1px solid #e0e0e0",
                              borderRadius: 2,
                            }}
                          >
                            <Table>
                              <TableBody>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      fontWeight: 400,
                                      width: "200px",
                                      bgcolor: "#f8f9fa",
                                      fontFamily: "Inter",
                                      fontSize: 14,
                                      color: "#666666",
                                    }}
                                  >
                                    Current Location
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 500,
                                      fontFamily: "Inter",
                                      fontSize: 14,
                                      color: "#333333",
                                    }}
                                  >
                                    {asset.current_location || "N/A"}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      fontWeight: 400,
                                      width: "200px",
                                      bgcolor: "#f8f9fa",
                                      fontFamily: "Inter",
                                      fontSize: 14,
                                      color: "#666666",
                                    }}
                                  >
                                    Location Domain
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 500,
                                      fontFamily: "Inter",
                                      fontSize: 14,
                                      color: "#333333",
                                    }}
                                  >
                                    {asset.current_location_domain || "N/A"}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      </Box>

                      {/* Batch & Dates Information */}
                      <Box width="100%">
                        <SimpleLabel
                          title="Batch & Dates"
                          fontWeight={600}
                          fontSize={16}
                          sx={{ mt: 2 }}
                        />
                        <Box mt={1.5}>
                          <TableContainer
                            component={Paper}
                            elevation={0}
                            sx={{
                              border: "1px solid #e0e0e0",
                              borderRadius: 2,
                            }}
                          >
                            <Table>
                              <TableBody>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      fontWeight: 400,
                                      width: "200px",
                                      bgcolor: "#f8f9fa",
                                      fontFamily: "Inter",
                                      fontSize: 14,
                                      color: "#666666",
                                    }}
                                  >
                                    Batch Number
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 500,
                                      fontFamily: "Inter",
                                      fontSize: 14,
                                      color: "#333333",
                                    }}
                                  >
                                    {asset.batch_number || "N/A"}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      fontWeight: 400,
                                      width: "200px",
                                      bgcolor: "#f8f9fa",
                                      fontFamily: "Inter",
                                      fontSize: 14,
                                      color: "#666666",
                                    }}
                                  >
                                    Date Received
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 500,
                                      fontFamily: "Inter",
                                      fontSize: 14,
                                      color: "#333333",
                                    }}
                                  >
                                    {formatDate(asset.date_received)}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      fontWeight: 400,
                                      width: "200px",
                                      bgcolor: "#f8f9fa",
                                      fontFamily: "Inter",
                                      fontSize: 14,
                                      color: "#666666",
                                    }}
                                  >
                                    Manufactured Date
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 500,
                                      fontFamily: "Inter",
                                      fontSize: 14,
                                      color: "#333333",
                                    }}
                                  >
                                    N/A
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      fontWeight: 400,
                                      width: "200px",
                                      bgcolor: "#f8f9fa",
                                      fontFamily: "Inter",
                                      fontSize: 14,
                                      color: "#666666",
                                    }}
                                  >
                                    Warranty (Months)
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 500,
                                      fontFamily: "Inter",
                                      fontSize: 14,
                                      color: "#333333",
                                    }}
                                  >
                                    {asset.warranty_in_months?.toString() ||
                                      "N/A"}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      </Box>

                      {/* Asset Metadata */}
                      {asset.asset_meta && asset.asset_meta.length > 0 && (
                        <Box width="100%">
                          <SimpleLabel
                            title="Additional Specifications"
                            fontWeight={600}
                            fontSize={16}
                            sx={{ mt: 2 }}
                          />
                          <Box mt={1.5}>
                            <TableContainer
                              component={Paper}
                              elevation={0}
                              sx={{
                                border: "1px solid #e0e0e0",
                                borderRadius: 2,
                              }}
                            >
                              <Table>
                                <TableBody>
                                  {asset.asset_meta.map((meta, metaIndex) => (
                                    <TableRow key={metaIndex}>
                                      <TableCell
                                        sx={{
                                          fontWeight: 400,
                                          width: "200px",
                                          bgcolor: "#f8f9fa",
                                          fontFamily: "Inter",
                                          fontSize: 14,
                                          color: "#666666",
                                        }}
                                      >
                                        {meta.asset_tag}
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          fontWeight: 500,
                                          fontFamily: "Inter",
                                          fontSize: 14,
                                          color: "#333333",
                                        }}
                                      >
                                        {meta.meta_value}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        </Box>
                      )}
                    </Stack>
                  </ExpandableContainer>
                </Box>
              ))}
            </>
          )}
        </Stack>
      </Box>

      {/* Image Modal */}
      <Modal
        open={!!imageModal}
        onClose={handleCloseModal}
        closeAfterTransition
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Fade in={!!imageModal}>
          <Box
            sx={{
              position: "relative",
              maxWidth: "90vw",
              maxHeight: "90vh",
              outline: "none",
            }}
          >
            <IconButton
              onClick={handleCloseModal}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                bgcolor: "white",
                zIndex: 1,
                "&:hover": {
                  bgcolor: "#f5f5f5",
                },
              }}
            >
              <CloseIcon />
            </IconButton>

            <Card
              sx={{
                boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Box
                component="img"
                src={imageModal?.url}
                alt={`Evidence ${imageModal?.index ? imageModal.index + 1 : ""}`}
                sx={{
                  maxWidth: "85vw",
                  maxHeight: "70vh",
                  display: "block",
                }}
              />
              <CardContent sx={{ bgcolor: "white", py: 2 }}>
                <Stack spacing={0.5}>
                  <SimpleLabel
                    title={`Image ${imageModal?.index ? imageModal.index + 1 : ""}`}
                    fontWeight={600}
                    fontSize={16}
                  />
                  <SimpleLabel
                    title={`Type: ${imageModal?.type || ""}`}
                    fontWeight={400}
                    fontSize={14}
                  />
                  <SimpleLabel
                    title={`Size: ${imageModal?.size ? (imageModal.size / 1024).toFixed(2) : "0"} KB`}
                    fontWeight={400}
                    fontSize={14}
                  />
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </Fade>
      </Modal>
    </MainPageBase>
  );
};

export { AssetReportDetailPage };
